import { action, computed, makeObservable } from 'mobx';

import { concatPath, dev, flattenError, localTime, moment, TimeFormat } from '@feathr/hooks';
import type { Attributes, TConstraints } from '@feathr/rachis';

import { CAMPAIGN_DEFAULT_MAX_BUDGET } from '../constants';
import { REGEX_INPUT_VALIDATION } from '../global';
import { ReportModel } from '../model';
import type { Campaigns } from './campaigns';
import type { ICampaignAttributes, TCampaignAttributes } from './types';
import { CampaignClass, CampaignState } from './types';

const {
  Affinity,
  AutoPinpointEmail,
  Conversation,
  Drip,
  DripStep,
  EmailList,
  EmailListFacebook,
  Facebook,
  LandingPage,
  Lookalike,
  MailrailAutoSend,
  MailrailDrip,
  MailrailPartnerMessage,
  MailrailSingleSend,
  MobileGeoFenceRetargeting,
  MobileGeoFencing,
  PinpointEmail,
  PinpointPartnerMessage,
  Referral,
  Search,
  SeedSegment,
  Segment,
  SmartPinpointEmail,
  TrackedLink,
  GoogleAdsSearch,
  GoogleAdsSmart,
  Omni,
} = CampaignClass;

const { Draft, Complete, Completing, Erroring, Published, Publishing, Scheduled, Stopped } =
  CampaignState;

export const CampaignLabelMap = new Map<CampaignClass, string>([
  [Affinity, 'Affinity'],
  [AutoPinpointEmail, 'Auto Send'],
  [Conversation, 'Conversation'],
  [Drip, 'Drip'],
  [DripStep, 'Drip Step'],
  [EmailList, 'Email Mapping'],
  [EmailListFacebook, 'Meta Email Mapping'],
  [Facebook, 'Meta Retargeting'],
  [GoogleAdsSearch, 'Google Ad Grants'],
  [GoogleAdsSmart, 'Google Ad Grants'],
  [LandingPage, 'Landing Page'],
  [Lookalike, 'Lookalike'],
  [MailrailAutoSend, 'Auto Send Email'],
  [MailrailDrip, 'Drip Email'],
  [MailrailSingleSend, 'Single Send Email'],
  [MailrailPartnerMessage, 'Partner Message'],
  [MobileGeoFenceRetargeting, 'Historical Geofencing'],
  [MobileGeoFencing, 'Mobile Geofencing'],
  [Omni, 'Pixl Plus'],
  [PinpointEmail, 'Single Send'],
  [Referral, 'Invites'],
  [Search, 'Search Keyword'],
  [SeedSegment, 'Lookalike'],
  [Segment, 'Retargeting'],
  [SmartPinpointEmail, 'Smart Send'],
  [TrackedLink, 'Tracked Link'],
]);

/** Campaign classes that support TTD (Trade Desk) integration */
export const TTD_CAMPAIGN_CLASSES: CampaignClass[] = [
  Affinity,
  EmailList,
  Lookalike,
  MobileGeoFenceRetargeting,
  MobileGeoFencing,
  Search,
  SeedSegment,
  Segment,
  Omni,
];

export function getMaxTargetValue(
  maxBudget = CAMPAIGN_DEFAULT_MAX_BUDGET,
  isMonetization = false,
): number {
  if (!isMonetization) {
    return maxBudget;
  }

  /*
   * Budgets must be less than the max budget or the equivalent number of impressions
   * at a $5CPM for a Monetization campaign.The equivalent number of
   * impressions is $5 / 1000 imps.
   * $10,000 * 1000 imp / $5 = 2,000,000 impressions.
   */
  return (maxBudget * 1000) / 5;
}

export function getMinDuration(): moment.Duration {
  return moment.duration(3, 'days');
}

export const baseConstraints: TConstraints<ICampaignAttributes> = {
  date_end: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    datetime: (value: string, attributes: any) => {
      if (attributes._cls === LandingPage) {
        return undefined;
      }
      if (attributes.date_start) {
        const dateStart = moment.utc(attributes.date_start, TimeFormat.isoDateTime);
        const dateEnd = moment.utc(value, TimeFormat.isoDateTime);
        const minDuration = getMinDuration();

        if (dateEnd.isBefore(dateStart)) {
          return {
            earliest: dateStart.add(minDuration).format(TimeFormat.isoDateTime),
            message: '^End date must be after start date.',
          };
        } else {
          return {
            earliest: dateStart.add(minDuration).format(TimeFormat.isoDateTime),
            message: `Campaigns of this type must be at least ${minDuration.asDays()} days long.`,
          };
        }
      }

      return true;
    },
    presence: (_, attributes) => {
      if (attributes._cls === LandingPage) {
        return undefined;
      }
      return {
        allowEmpty: false,
        message: '^End date cannot be empty.',
      };
    },
  },
  state: {
    inclusion: {
      within: [Draft, Complete, Completing, Erroring, Published, Publishing, Scheduled, Stopped],
      message: '^The campaign is not in a valid state.',
    },
  },
  name: {
    exclusion: {
      within: ['New Draft Campaign'],
      message: '^Change the default name for this campaign.',
    },
    presence: {
      allowEmpty: false,
    },
    format: {
      pattern: REGEX_INPUT_VALIDATION,
      flags: 'i',
      message: '^Campaign names must consist of letters, numbers, and basic symbols.',
    },
  },
};

export abstract class Campaign<
  IAttributes extends ICampaignAttributes = ICampaignAttributes,
> extends ReportModel<IAttributes> {
  public reportKey = 'c' as const;
  public className = 'campaign' as const;
  public override collection: Campaigns<this> | null = null;

  private _constraints?: TConstraints<IAttributes>;

  public get constraints(): TConstraints<IAttributes> {
    // Assembled once per instance and reused. Caching is safe because the only time-sensitive
    // value, date_start's `earliest`, is produced inside the datetime validator, which validate.js
    // invokes fresh on every validation rather than when this object is built.
    if (!this._constraints) {
      const constraints: TConstraints<ICampaignAttributes> = {
        ...baseConstraints,
        date_start: {
          datetime: (_: string, attributes: Partial<TCampaignAttributes>) => {
            if (attributes.state === Draft) {
              const minDaysAdvance = this.getMinDaysAdvance();
              return {
                earliest: moment.utc().add(minDaysAdvance, 'days').format(TimeFormat.isoDateTime),
                message:
                  minDaysAdvance === 0
                    ? '^Start date cannot be in the past'
                    : `^Campaigns of this type must be made at least ${minDaysAdvance} day${
                        minDaysAdvance > 1 ? 's' : ''
                      } in advance`,
              };
            }
            return true;
          },
          presence: {
            allowEmpty: false,
            message: '^Start date cannot be empty',
          },
        },
      };
      this._constraints = constraints;
    }
    return this._constraints;
  }

  /**
   * Minimum number of days in advance a campaign of this class must be scheduled. Lives on the
   * class rather than the instance so it is readable without constructing a model (e.g. from a
   * bare CampaignClass in the create wizard) and is safe to use from getDefaults, which runs
   * during construction before attributes exist. Ad classes need platform lead time and override
   * this on DisplayCampaign and again on subtypes that require even more lead time; other classes
   * opt out of the lead time constraint entirely by overriding the date_start constraint.
   */
  public static readonly minDaysAdvance: number = 0;

  /** Earliest valid start date for a campaign of this class. */
  public static getMinStartDate(): moment.Moment {
    return moment.utc().add(this.minDaysAdvance, 'days').endOf('day');
  }

  public getMinDaysAdvance(): number {
    return (this.constructor as typeof Campaign).minDaysAdvance;
  }

  public getMinStartDate(): moment.Moment {
    return (this.constructor as typeof Campaign).getMinStartDate();
  }

  constructor(attributes: Partial<IAttributes> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public override set(patch: Partial<Attributes<this>>, dirty = true): void {
    super.set(patch, dirty);
  }

  public getItemUrl(pathSuffix?: string): string {
    const localPathSuffix = pathSuffix ?? (this.get('_cls') === Conversation ? 'edit' : undefined);

    const flightId = this.get('flight');
    if (flightId) {
      return concatPath(
        `/projects/${this.get('event')}/flights/${flightId}/campaigns/${this.id}`,
        localPathSuffix,
      );
    }
    // Drip Step campaigns should route to the Drip campaign page
    const id = this.get('_cls') === DripStep ? this.get('drip_campaign') : this.id;

    return concatPath(`/projects/${this.get('event')}/campaigns/${id}`, localPathSuffix);
  }

  // Clone a campaign using the Campaigns collection custom cloneCampaign method
  public cloneCampaign(data: Partial<IAttributes>): Promise<void> {
    this.checkReplaced();
    this.assertCollection(this.collection);

    if (this.id) {
      const conversionTrackingMode = this.get('conversion_tracking_mode');
      const cloneData = {
        ...data,
        conversion_tracking_mode: conversionTrackingMode,
        ...(conversionTrackingMode === 'advanced' && {
          conversion_goals: this.get('conversion_goals'),
        }),
      } as Partial<Attributes<this>>;

      return this.collection.cloneCampaign(this.id, cloneData);
    }

    throw new Error('Models need to have ids to clone');
  }

  public static readonly defaults: Partial<ICampaignAttributes> = {
    // Defaults match what gets auto added on the backend
    exposure_settings: {
      target_cap_daily: 0,
      custom_target: false,
      recommended_target_percent: undefined,
      target_type: 'fixed_budget',
      target_value: undefined,
      pace: 'pace_to_end_of_flight',
    },
    is_enabled: true,
    name: '',
    parent_kind: 'event',
    rerun: false,
    state: CampaignState.Draft,
  };

  public override getDefaults(): Partial<IAttributes> {
    const now = moment.utc().format(TimeFormat.isoDateTime);
    return {
      ...super.getDefaults(),
      ...Campaign.defaults,
      // Date defaults are computed per construction so they don't grow stale during long sessions
      date_created: now,
      date_last_modified: now,
      date_start: this.getMinStartDate().format(TimeFormat.isoDateTime),
    } as Partial<IAttributes>;
  }

  /**
   * Throws if the campaign isn't valid to publish, logging the offending fields in dev.
   */
  private assertIsValidToPublish(): void {
    if (this.isValid(['_cls', ...Object.keys(this.constraints)], false)) {
      return;
    }
    if (dev.isActive) {
      const errors = this.validate([], false, 'grouped').errors;
      const flattenedErrors = Object.entries(errors).reduce<string[]>((acc, [key, error]) => {
        const keyErrors = flattenError(error);
        return keyErrors.length ? [...acc, `${key}: ${keyErrors.join('; ')}`] : acc;
      }, []);

      dev.error(`${this.constructor.name} is invalid`, flattenedErrors.join('\n'));
    }
    throw new Error('Campaign is invalid');
  }

  @action.bound
  public async publish(validate = true): Promise<Campaign> {
    if (!this.collection) {
      throw new Error('Campaign is not in a collection.');
    }
    if (validate) {
      this.assertIsValidToPublish();
    }
    return this.collection.publish(this);
  }

  /**
   * Publishes the campaign by PATCHing its pending edits and its new `state` together, instead of
   * going through the dedicated publish endpoint.
   *
   * That endpoint takes no body, so `publish` can only carry the state change — any edits have to
   * be sent ahead of it as their own PATCH. The backend rejects such a PATCH on a stopped or
   * completed Mailrail campaign unless it carries the state change itself, so re-publishing one
   * that has pending edits has to arrive as a single request. The regular PATCH endpoint maps
   * `state: 'publishing'` onto the same publish call.
   *
   * The new state isn't applied locally ahead of the response: when the request fails the
   * campaign stays stopped, which is what the editor should go on showing.
   */
  @action.bound
  public async republish(validate = true): Promise<Campaign> {
    if (validate) {
      this.assertIsValidToPublish();
    }
    return this.patch({
      ...this.toDirtyJS(),
      state: CampaignState.Publishing,
    } as Partial<IAttributes>);
  }

  /**
   * TODO: the optimistic `set` is never rolled back, and `Collection.patch` resolves on a failed
   * request rather than throwing. So a failed stop leaves the campaign reading as stopped locally
   * while the server still has it published, with `state` left dirty. `republish` avoids this by
   * not applying the state ahead of the response.
   */
  public async stop(): Promise<Campaign> {
    const patch = { state: Stopped } as Partial<Attributes<this>>;
    this.set(patch);
    return this.patch(patch);
  }

  /**
   * Move this campaign to a different project.
   * @param destinationProjectId - The ID of the project to move this campaign to.
   */
  public async move(destinationProjectId: string): Promise<this> {
    this.checkReplaced();
    this.assertCollection(this.collection);

    if (this.id) {
      return this.collection.moveCampaign(this.id, destinationProjectId) as Promise<this>;
    }

    throw new Error('Models need to have ids to move');
  }

  @computed
  public get isDraft(): boolean {
    return this.get('state') === Draft;
  }

  @computed
  public get isScheduled(): boolean {
    return this.get('state') === Scheduled;
  }

  /**
   * Whether the campaign has ever been published to its ad platform, based on
   * the `date_first_published` timestamp. Stays true after the campaign is
   * stopped or errors, because the published ad-platform structure persists.
   */
  @computed
  public get hasBeenPublished(): boolean {
    return !!this.get('date_first_published');
  }

  @computed
  public get isPublishing(): boolean {
    return this.get('state') === Publishing;
  }

  @computed
  public get isPublished(): boolean {
    return this.get('state') === Published;
  }

  @computed
  public get isStopped(): boolean {
    return this.get('state') === Stopped;
  }

  @computed
  public get isCompleting(): boolean {
    return this.get('state') === Completing;
  }

  @computed
  public get isComplete(): boolean {
    return this.get('state') === Complete;
  }

  @computed
  public get isErroring(): boolean {
    return this.get('state') === Erroring;
  }

  @computed
  public get isDisabledCampaignState(): boolean {
    return this.get('state') === Published || this.get('state') === Stopped;
  }

  @computed
  public get localStartTime(): string {
    return localTime(this.get('date_start'));
  }

  @computed
  public get localEndTime(): string {
    return localTime(this.get('date_end'));
  }

  @computed
  public get isCreativesCampaign(): boolean {
    return [...TTD_CAMPAIGN_CLASSES, EmailListFacebook, Facebook].includes(this.get('_cls'));
  }

  @computed
  public get isGeofencingCampaign(): boolean {
    return [MobileGeoFenceRetargeting, MobileGeoFencing].includes(this.get('_cls'));
  }

  @computed
  public get isMobileGeoFencingCampaign(): boolean {
    return this.get('_cls') === MobileGeoFencing;
  }

  @computed
  public get isEmailMappingCampaign(): boolean {
    return [EmailList, EmailListFacebook].includes(this.get('_cls'));
  }

  @computed
  public get isAwarenessCampaign(): boolean {
    return [Affinity, Search, Lookalike, MobileGeoFenceRetargeting, MobileGeoFencing].includes(
      this.get('_cls'),
    );
  }

  /**
   * Getter that returns whether or not the campaign is an ad campaign.
   * Uses campaign.isCreativesCampaign, but is written in this way
   * for clarity in other parts of the app.
   */
  @computed
  public get isAdCampaign(): boolean {
    return this.isCreativesCampaign;
  }

  @computed
  public get isTTDCampaign(): boolean {
    return TTD_CAMPAIGN_CLASSES.includes(this.get('_cls'));
  }

  @computed
  public get isSegmentCampaign(): boolean {
    return [Segment, Facebook, Omni].includes(this.get('_cls'));
  }

  @computed
  public get isRetargetingCampaign(): boolean {
    return [Segment, Facebook].includes(this.get('_cls'));
  }

  /**
   * Whether this is a Search Keyword campaign, which builds its audience from an uploaded
   * keyword list. Not to be confused with a Google Ads search campaign, which keeps its
   * keywords on its ad groups.
   */
  @computed
  public get isSearchKeywordCampaign(): boolean {
    return [Search].includes(this.get('_cls'));
  }

  @computed
  public get isOmniCampaign(): boolean {
    return [Omni].includes(this.get('_cls'));
  }

  @computed
  public get isOmniReachCampaign(): boolean {
    return this.isOmniCampaign && this.get('objective') === 'reach';
  }

  @computed
  public get isLandingPageCampaign(): boolean {
    return [LandingPage].includes(this.get('_cls'));
  }

  @computed
  public get isAffinityCampaign(): boolean {
    return [Affinity].includes(this.get('_cls'));
  }

  @computed
  public get isConversationCampaign(): boolean {
    return [Conversation].includes(this.get('_cls'));
  }

  @computed
  public get name(): string {
    const label = CampaignLabelMap.get(this.get('_cls'));
    return (
      this.get('name', '' as Exclude<IAttributes['name'], undefined>).trim() ||
      `Unnamed ${label ? label + ' ' : ''}Campaign`
    );
  }

  @computed
  public get isMonetization(): boolean {
    return (
      this.get('parent_kind') === 'partner' &&
      this.get('exposure_settings').target_type === 'fixed_impressions'
    );
  }

  @computed
  public get isFacebook(): boolean {
    return [Facebook, EmailListFacebook].includes(this.get('_cls'));
  }

  @computed
  public get isGoogle(): boolean {
    return [GoogleAdsSmart, GoogleAdsSearch].includes(this.get('_cls'));
  }

  @computed
  public get isPinpointSingleSend(): boolean {
    return this.get('_cls') === PinpointEmail;
  }

  @computed
  public get isAnyEmail(): boolean {
    return this.isPinpointEmail || this.isMailrailEmail;
  }

  @computed
  public get isAnyPartnerMessage(): boolean {
    return [MailrailPartnerMessage, PinpointPartnerMessage].includes(this.get('_cls'));
  }

  @computed
  public get isPinpointDrip(): boolean {
    return this.get('_cls') === Drip;
  }

  @computed
  public get isDonation(): boolean {
    return this.get('is_donation');
  }

  @computed
  public get isPinpoint(): boolean {
    return [
      PinpointEmail,
      SmartPinpointEmail,
      AutoPinpointEmail,
      Drip,
      DripStep,
      PinpointPartnerMessage,
    ].includes(this.get('_cls'));
  }

  @computed
  public get isPinpointEmail(): boolean {
    return this.isPinpoint && !this.isAnyPartnerMessage;
  }

  @computed
  public get isMailrailEmail(): boolean {
    return [MailrailAutoSend, MailrailDrip, MailrailSingleSend].includes(this.get('_cls'));
  }

  @computed
  public get isMailrailTriggerable(): boolean {
    return [MailrailAutoSend, MailrailDrip].includes(this.get('_cls'));
  }

  @computed
  public get isMailrailAutoSend(): boolean {
    return this.get('_cls') === MailrailAutoSend;
  }

  @computed
  public get isMailrailDrip(): boolean {
    return this.get('_cls') === MailrailDrip;
  }

  @computed
  public get isMailrailSingleSend(): boolean {
    return this.get('_cls') === MailrailSingleSend;
  }

  @computed
  public get isMailrailPartnerMessage(): boolean {
    return this.get('_cls') === MailrailPartnerMessage;
  }

  /**
   * Getter that checks if a campaign was created after the date
   * scripted updates was merged in. Campaigns that were live before
   * and after the merge may have statistical discrepancies across
   * report components due to changes in calculation methodology.
   */
  @computed
  public get isCreatedAfterScriptedUpdates(): boolean {
    return moment(this.get('date_start')).isAfter(moment('02-21-2023', 'MM-DD-YYYY'));
  }

  public isAfterDateSendStart({
    now = moment.utc(),
    offset = 0,
    offsetUnit = 'seconds',
  }: {
    now?: moment.Moment;
    offset?: number;
    offsetUnit?: moment.unitOfTime.DurationConstructor;
  } = {}): boolean {
    const sendStart = moment.utc(this.get('date_send_start'), moment.ISO_8601);
    return now.isSameOrAfter(sendStart.add(offset, offsetUnit));
  }

  /**
   * Getter that checks if an email campaign is past its end date
   * We offset 1 hour by default to allow time for the num_targeted stat
   * to finish processing before we display it.
   */
  public get isAfterDateSendEnd(): boolean {
    const now = moment.utc();
    const sendEnd = this.get('date_send_end');
    if (!sendEnd) {
      return false;
    }
    const sendEndMoment = moment.utc(sendEnd, moment.ISO_8601);
    return now.isSameOrAfter(sendEndMoment.add(1, 'hour'));
  }

  /**
   * Getter that checks if a non-Pinpoint campaign is past its end date.
   */
  public get isAfterDateEnd(): boolean {
    const now = moment.utc();
    const end = this.get('date_end');
    if (!end) {
      return false;
    }
    // date_end is a UTC instant stored without an offset; parse it as UTC so the comparison isn't
    // shifted by the local timezone (moment() would otherwise treat it as local time).
    return now.isSameOrAfter(moment.utc(end, moment.ISO_8601));
  }

  /**
   * Getter that checks if a non-Pinpoint campaign is past its start date
   */
  public get isAfterDateStart(): boolean {
    const now = moment.utc();
    const start = this.get('date_start');
    if (!start) {
      return false;
    }
    // date_start is a UTC instant stored without an offset; parse it as UTC so the comparison
    // isn't shifted by the local timezone (moment() would otherwise treat it as local time).
    return now.isSameOrAfter(moment.utc(start, moment.ISO_8601));
  }

  /**
   * Getter that checks if any campaign type is past its end date.
   */
  public get isPastEndDate(): boolean {
    return this.isPinpointEmail ? this.isAfterDateSendEnd : this.isAfterDateEnd;
  }

  public get isPastStartDate(): boolean {
    return this.isPinpointEmail ? this.isAfterDateSendStart() : this.isAfterDateStart;
  }
}

export class BaseCampaign extends Campaign<ICampaignAttributes> {}
